import { useState, useEffect, useCallback } from "react";
import { PaperPlaneRight, Tray } from "@phosphor-icons/react";
import { toast } from "sonner";
import dayjs from "dayjs";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MessageList } from "@/pages/Contact";
import { useSeo } from "@/lib/seo";

export default function AdminInbox() {
  useSeo("Admin Inbox", "Reply to creator messages.");
  const { user, loading } = useAuth();
  const [convs, setConvs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");

  const loadConvs = useCallback(() => api.get("/admin/conversations").then((r) => setConvs(r.data)).catch(() => {}), []);
  const loadThread = useCallback((cid) => api.get(`/admin/conversations/${cid}`).then((r) => setMessages(r.data.messages)).catch(() => {}), []);

  useEffect(() => {
    if (!user?.isAdmin) return;
    loadConvs();
    const t = setInterval(() => {
      loadConvs();
      if (selected) loadThread(selected.id);
    }, 5000);
    return () => clearInterval(t);
  }, [user, selected, loadConvs, loadThread]);

  const open = (c) => {
    setSelected(c);
    loadThread(c.id);
  };

  const reply = async (e) => {
    e.preventDefault();
    if (!text.trim() || !selected) return;
    try {
      await api.post(`/admin/conversations/${selected.id}/reply`, { text });
      setText("");
      loadThread(selected.id);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  if (loading) return null;
  if (!user?.isAdmin) {
    return (
      <div className="p-16 text-center" data-testid="admin-denied">
        <p className="font-bold">Admin access only</p>
        <p className="text-[var(--muted)] text-sm mt-1">Sign in with the admin Google account to view the inbox.</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10 lg:py-14" data-testid="admin-inbox-page">
      <div className="fade-up mb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-[var(--coral)] font-semibold mb-2">Support</p>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tight">Admin Inbox</h1>
      </div>
      <div className="grid md:grid-cols-[280px_1fr] gap-4 fade-up-1">
        <div className="surface rounded-[20px] p-3 space-y-1.5 max-h-[560px] overflow-y-auto" data-testid="conversations-list">
          {convs.length === 0 && <p className="text-[var(--muted)] text-sm text-center py-8">No conversations yet.</p>}
          {convs.map((c) => (
            <button
              key={c.id}
              data-testid={`conv-row-${c.id}`}
              onClick={() => open(c)}
              className={`w-full text-left rounded-[12px] px-3 py-2.5 transition-colors ${selected?.id === c.id ? "bg-[var(--glow)]/15 border border-[var(--glow)]/40" : "hover:bg-[var(--bg)] border border-transparent"}`}
            >
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold truncate">{c.name}</p>
                {c.unreadForAdmin && <span className="w-2 h-2 rounded-full bg-[var(--coral)] shrink-0" data-testid={`unread-dot-${c.id}`} />}
              </div>
              <p className="text-xs text-[var(--muted)] truncate mt-0.5">{c.lastMessage || "…"}</p>
              <p className="text-[9px] text-[var(--muted)] mt-0.5">{dayjs(c.lastMessageAt).format("MMM D, HH:mm")}{c.email ? ` · ${c.email}` : ""}</p>
            </button>
          ))}
        </div>
        <div className="surface rounded-[20px] flex flex-col h-[560px]">
          {selected ? (
            <>
              <div className="px-4 py-3 border-b border-[var(--border-c)]">
                <p className="text-sm font-semibold">{selected.name}</p>
                {selected.email && <p className="text-[10px] text-[var(--muted)]">{selected.email}</p>}
              </div>
              <MessageList messages={messages} mineIs="admin" />
              <form onSubmit={reply} className="p-3 border-t border-[var(--border-c)] flex gap-2">
                <input
                  data-testid="admin-reply-input"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Reply as VidhFlow..."
                  className="flex-1 bg-[var(--bg)] border border-[var(--border-c)] rounded-full px-4 py-2.5 text-sm outline-none focus:border-[var(--glow)]"
                />
                <button data-testid="admin-reply-btn" className="w-10 h-10 rounded-full bg-[var(--glow)] text-white flex items-center justify-center hover:bg-[#6a4ce6] transition-colors">
                  <PaperPlaneRight size={16} weight="bold" />
                </button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-[var(--muted)]">
              <Tray size={32} weight="duotone" />
              <p className="text-sm mt-2">Select a conversation</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
